import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Visibility, VisibilityOff, Lock, Person, CheckCircle, Cancel } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { RegisterUserDTO } from '../dtos';
import { validatePassword, PASSWORD_HELPER_TEXT } from '../utils/passwordValidation';

interface NewPasswordFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string;
  onSubmit: (data: RegisterUserDTO) => Promise<void>;
}

interface NewPasswordFormData {
  firstName: string;
  lastName: string;
  newPassword: string;
  confirmPassword: string;
}

export const NewPasswordForm: React.FC<NewPasswordFormProps> = ({
  onSuccess,
  onCancel,
  isLoading = false,
  error,
  onSubmit,
}) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [passwordFocused, setPasswordFocused] = React.useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset,
  } = useForm<NewPasswordFormData>({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const password = watch('newPassword');
  const passwordValidation = React.useMemo(() => validatePassword(password || ''), [password]);

  const handleFormSubmit = async (data: NewPasswordFormData) => {
    try {
      await onSubmit({
        firstName: data.firstName,
        lastName: data.lastName,
        newPassword: data.newPassword,
      });
      reset();
      onSuccess();
    } catch (error) {
      console.error('Password setup failed:', error);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(handleFormSubmit)}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: 'fit-content',
          py: 2,
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom textAlign="center">
          Set Your Password
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
          Complete your account setup by setting a new password and providing your name
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        <Controller
          name="firstName"
          control={control}
          rules={{
            required: 'First name is required',
            minLength: {
              value: 2,
              message: 'First name must be at least 2 characters',
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="First Name"
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Controller
          name="lastName"
          control={control}
          rules={{
            required: 'Last name is required',
            minLength: {
              value: 2,
              message: 'Last name must be at least 2 characters',
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Last Name"
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Controller
          name="newPassword"
          control={control}
          rules={{
            required: 'Password is required',
            validate: (value) => {
              const validation = validatePassword(value);
              if (!validation.isValid) {
                return 'Password does not meet requirements';
              }
              return true;
            },
          }}
          render={({ field }) => (
            <>
              <TextField
                {...field}
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
                margin="dense"
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {(passwordFocused || password) && (
                <Box
                  sx={{
                    mt: 1,
                    mb: 1,
                    p: 1.5,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
                    Password Requirements:
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    <ListItem sx={{ py: 0, minHeight: 28 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {passwordValidation.checks.minLength ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Cancel color="error" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary="At least 8 characters"
                        primaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0, minHeight: 28 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {passwordValidation.checks.hasLetter ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Cancel color="error" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary="Contains at least one letter"
                        primaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0, minHeight: 28 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {passwordValidation.checks.hasNumberOrSymbol ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Cancel color="error" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary="Contains at least one number or special character"
                        primaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  </List>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {PASSWORD_HELPER_TEXT}
                  </Typography>
                </Box>
              )}
            </>
          )}
        />

        <Controller
          name="confirmPassword"
          control={control}
          rules={{
            required: 'Please confirm your password',
            validate: (value) => value === password || 'Passwords do not match',
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={toggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            type="button"
            fullWidth
            variant="outlined"
            size="large"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={!isValid || isLoading}
          >
            {isLoading ? 'Setting Password...' : 'Set Password'}
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
};
